// src/components/DialogMovimientosProducto.js
//
// Dialog con dos tabs:
//   Kardex — historial cronológico de movimientos del producto en el
//            almacén activo (o consolidado si el usuario elige).
//   Movimiento nuevo — botones Entrada / Salida / Ajuste / Transferencia
//                      con sus respectivos sub-forms.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Tabs, Tab, Box,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  IconButton, Typography, Stack, Chip, Button, MenuItem, TextField,
  Alert, Grid, CircularProgress, InputAdornment, Divider, Tooltip,
} from '@mui/material';
import CloseIcon             from '@mui/icons-material/Close';
import RefreshIcon           from '@mui/icons-material/Refresh';
import AddCircleIcon         from '@mui/icons-material/AddCircleOutline';
import RemoveCircleIcon      from '@mui/icons-material/RemoveCircleOutline';
import TuneIcon              from '@mui/icons-material/Tune';
import SwapHorizIcon         from '@mui/icons-material/SwapHoriz';
import ArrowDownwardIcon     from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon       from '@mui/icons-material/ArrowUpward';
import ContentPasteIcon      from '@mui/icons-material/ContentPaste';
import { movimientos as apiMov, almacenes as apiAlm } from '../api/posApi';

const TabPanel = ({ value, index, children }) => (
  <Box hidden={value !== index} sx={{ pt: 2 }}>{value === index && children}</Box>
);

const fmtNum   = (n) => (Number(n) || 0).toLocaleString('es-CO', { maximumFractionDigits: 2 });
const fmtCOP   = (n) => (Number(n) || 0).toLocaleString('es-CO',
  { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });
const fmtFecha = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return s; }
};

const DialogMovimientosProducto = ({ producto, empresaId, sucursales, onClose, onDidChange, notify }) => {
  const [tab, setTab]                 = useState(0);
  const [almacenes, setAlmacenes]     = useState([]);
  const [almacenId, setAlmacenId]     = useState(null);
  const [kardex, setKardex]           = useState([]);
  const [cargando, setCargando]       = useState(false);
  const [tipoMov, setTipoMov]         = useState(null); // 'ENTRADA'|'SALIDA'|'AJUSTE'|'TRANSFERENCIA'|null

  const productoId = producto?.productoId ?? producto?.ProductoId;
  const nombreProd = producto?.nombre ?? producto?.Nombre;

  const cargarAlmacenes = useCallback(async () => {
    // Trae almacenes de TODAS las sucursales de la empresa (útil para
    // consolidado y para elegir destino en transferencia).
    if (!sucursales || sucursales.length === 0) return;
    try {
      const listas = await Promise.all(sucursales.map(s => apiAlm.listar(s.sucursalId ?? s.SucursalId)));
      const flat = listas.flat().filter(Boolean);
      // Nombre visible incluye sucursal para claridad
      const conNombre = flat.map(a => {
        const suc = sucursales.find(s => (s.sucursalId ?? s.SucursalId) === a.sucursalId);
        return { ...a, nombreCompleto: suc ? `${a.nombre} · ${suc.nombre ?? suc.Nombre}` : a.nombre };
      });
      setAlmacenes(conNombre);
      if (!almacenId && conNombre.length > 0) setAlmacenId(conNombre[0].almacenId);
    } catch { /* silent */ }
  }, [sucursales, almacenId]);

  useEffect(() => { cargarAlmacenes(); }, [cargarAlmacenes]);

  const cargarKardex = useCallback(async () => {
    if (!empresaId || !productoId) return;
    setCargando(true);
    try {
      const r = await apiMov.kardex(productoId, empresaId, { almacenId: almacenId || undefined, limite: 200 });
      setKardex(Array.isArray(r) ? r : []);
    } catch (e) {
      notify(e.mensajeUsuario || 'No se pudo cargar el kardex.', 'error');
    } finally { setCargando(false); }
  }, [empresaId, productoId, almacenId, notify]);

  useEffect(() => { if (tab === 0) cargarKardex(); }, [tab, cargarKardex]);

  const stockActual = kardex[0]?.saldoDespues ?? 0;

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg"
            PaperProps={{ sx: { minHeight: 600 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={700} noWrap>
            Movimientos: {nombreProd}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
            <Chip size="small" label={`Código: ${producto?.codigo ?? producto?.Codigo}`} variant="outlined" />
            {almacenId && kardex.length > 0 && (
              <Chip size="small" color="primary"
                    label={`Saldo actual: ${fmtNum(stockActual)}`} />
            )}
          </Stack>
        </Box>
        <TextField select size="small" label="Almacén"
                   value={almacenId || ''} onChange={(e) => setAlmacenId(Number(e.target.value) || null)}
                   sx={{ minWidth: 260, mr: 1 }}>
          <MenuItem value="">(consolidado)</MenuItem>
          {almacenes.map(a => (
            <MenuItem key={a.almacenId} value={a.almacenId}>{a.nombreCompleto}</MenuItem>
          ))}
        </TextField>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tab icon={<ContentPasteIcon fontSize="small" />} iconPosition="start" label="Kardex" />
        <Tab icon={<AddCircleIcon fontSize="small" />} iconPosition="start" label="Nuevo movimiento" />
      </Tabs>
      <DialogContent>
        {/* ── KARDEX ── */}
        <TabPanel value={tab} index={0}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
              Últimos 200 movimientos {almacenId ? 'del almacén' : 'de todos los almacenes'}.
            </Typography>
            <Tooltip title="Actualizar">
              <IconButton size="small" onClick={cargarKardex}><RefreshIcon /></IconButton>
            </Tooltip>
          </Stack>
          {cargando ? (
            <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : kardex.length === 0 ? (
            <Alert severity="info">Sin movimientos registrados.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Motivo</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">Saldo</TableCell>
                    <TableCell align="right">Costo unit.</TableCell>
                    <TableCell>Documento</TableCell>
                    {!almacenId && <TableCell>Almacén</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {kardex.map(k => (
                    <TableRow key={k.movimientoId}>
                      <TableCell><Typography variant="caption">{fmtFecha(k.fecha)}</Typography></TableCell>
                      <TableCell>
                        <Chip size="small" label={k.tipoMovimiento}
                              color={k.tipoMovimiento === 'ENTRADA' ? 'success'
                                   : k.tipoMovimiento === 'SALIDA'  ? 'error'
                                   : k.tipoMovimiento === 'TRANSFERENCIA' ? 'info'
                                   : 'default'}
                              variant="outlined" />
                      </TableCell>
                      <TableCell><Typography variant="caption">{k.motivo || '—'}</Typography></TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                          {k.cantidad > 0
                            ? <ArrowUpwardIcon color="success" sx={{ fontSize: 14 }} />
                            : <ArrowDownwardIcon color="error" sx={{ fontSize: 14 }} />}
                          <span>{fmtNum(Math.abs(k.cantidad))}</span>
                        </Stack>
                      </TableCell>
                      <TableCell align="right"><b>{fmtNum(k.saldoDespues)}</b></TableCell>
                      <TableCell align="right">{fmtCOP(k.costoUnitario)}</TableCell>
                      <TableCell><Typography variant="caption">{k.documentoNumero || '—'}</Typography></TableCell>
                      {!almacenId && (
                        <TableCell><Typography variant="caption">{k.almacenNombre || '—'}</Typography></TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* ── NUEVO MOVIMIENTO ── */}
        <TabPanel value={tab} index={1}>
          {!tipoMov ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Button fullWidth variant="outlined" size="large" startIcon={<AddCircleIcon />}
                        onClick={() => setTipoMov('ENTRADA')} color="success"
                        sx={{ py: 3, flexDirection: 'column' }}>
                  Entrada<Typography variant="caption">Compra o entrada manual</Typography>
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button fullWidth variant="outlined" size="large" startIcon={<RemoveCircleIcon />}
                        onClick={() => setTipoMov('SALIDA')} color="error"
                        sx={{ py: 3, flexDirection: 'column' }}>
                  Salida<Typography variant="caption">Merma, consumo, robo</Typography>
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Button fullWidth variant="outlined" size="large" startIcon={<TuneIcon />}
                        onClick={() => setTipoMov('AJUSTE')}
                        sx={{ py: 3, flexDirection: 'column' }}>
                  Ajuste<Typography variant="caption">Fijar stock exacto</Typography>
                </Button>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Tooltip title={sucursales?.length < 2 ? 'Necesitas más de un almacén para transferir' : ''}>
                  <span>
                    <Button fullWidth variant="outlined" size="large" startIcon={<SwapHorizIcon />}
                            onClick={() => setTipoMov('TRANSFERENCIA')} color="info"
                            disabled={almacenes.length < 2}
                            sx={{ py: 3, flexDirection: 'column' }}>
                      Transferencia
                      <Typography variant="caption">Entre almacenes (con autorización)</Typography>
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  Las <b>transferencias</b> requieren autorización de un segundo usuario.
                  Quedan pendientes hasta ser aprobadas o rechazadas.
                </Alert>
              </Grid>
            </Grid>
          ) : (
            <FormMovimiento
              tipo={tipoMov} productoId={productoId} empresaId={empresaId}
              almacenes={almacenes} almacenActivo={almacenId}
              onCancel={() => setTipoMov(null)}
              onSaved={async (msg) => {
                setTipoMov(null);
                await cargarKardex();
                setTab(0);
                notify(msg, 'success');
                onDidChange?.();
              }}
              onError={(m) => notify(m, 'error')}
            />
          )}
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

// ══════════════════════════════════════════════════════════════════
// Form movimientos
// ══════════════════════════════════════════════════════════════════

function FormMovimiento({ tipo, productoId, empresaId, almacenes, almacenActivo, onCancel, onSaved, onError }) {
  const [f, setF] = useState({
    almacenId: almacenActivo || almacenes[0]?.almacenId,
    almacenDestinoId: almacenes.find(a => a.almacenId !== almacenActivo)?.almacenId,
    cantidad: '',
    costoUnitario: '',
    motivo: tipo === 'ENTRADA' ? 'COMPRA' :
            tipo === 'SALIDA'  ? 'MERMA'  : '',
    documentoNumero: '',
    stockFinalContado: '',
    notas: '',
  });
  const [enviando, setEnviando] = useState(false);
  const upd = (k) => (e) => setF(s => ({ ...s, [k]: e.target.value }));

  const submit = async () => {
    setEnviando(true);
    try {
      if (tipo === 'ENTRADA') {
        await apiMov.entrada(empresaId, {
          almacenId: Number(f.almacenId), productoId,
          cantidad: parseFloat(f.cantidad) || 0,
          costoUnitario: f.costoUnitario === '' ? null : parseFloat(f.costoUnitario),
          motivo: f.motivo || null,
          documentoNumero: f.documentoNumero || null,
          proveedorId: null,
        });
        onSaved('Entrada registrada.');
      } else if (tipo === 'SALIDA') {
        await apiMov.salida(empresaId, {
          almacenId: Number(f.almacenId), productoId,
          cantidad: parseFloat(f.cantidad) || 0,
          motivo: f.motivo || null,
          notas: f.notas || null,
        });
        onSaved('Salida registrada.');
      } else if (tipo === 'AJUSTE') {
        await apiMov.ajuste(empresaId, {
          almacenId: Number(f.almacenId), productoId,
          stockFinalContado: parseFloat(f.stockFinalContado) || 0,
          notas: f.notas || null,
        });
        onSaved('Ajuste registrado.');
      } else if (tipo === 'TRANSFERENCIA') {
        await apiMov.transferencia(empresaId, {
          almacenOrigenId: Number(f.almacenId),
          almacenDestinoId: Number(f.almacenDestinoId),
          productoId,
          cantidad: parseFloat(f.cantidad) || 0,
          notas: f.notas || null,
        });
        onSaved('Transferencia creada. Pendiente de aprobación por otro usuario.');
      }
    } catch (e) {
      onError(e.mensajeUsuario || 'No se pudo registrar el movimiento.');
    } finally { setEnviando(false); }
  };

  const canSubmit = () => {
    if (!f.almacenId) return false;
    if (tipo === 'AJUSTE') return f.stockFinalContado !== '' && !isNaN(parseFloat(f.stockFinalContado));
    if (tipo === 'TRANSFERENCIA') return f.almacenDestinoId && f.cantidad > 0 && f.almacenDestinoId !== f.almacenId;
    return parseFloat(f.cantidad) > 0;
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Chip label={tipo} color={tipo === 'ENTRADA' ? 'success' : tipo === 'SALIDA' ? 'error' :
                                  tipo === 'TRANSFERENCIA' ? 'info' : 'default'} />
        <Button size="small" onClick={onCancel}>Cambiar tipo</Button>
      </Stack>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField select fullWidth
                     label={tipo === 'TRANSFERENCIA' ? 'Almacén origen' : 'Almacén'}
                     value={f.almacenId || ''} onChange={upd('almacenId')}>
            {almacenes.map(a => (
              <MenuItem key={a.almacenId} value={a.almacenId}>{a.nombreCompleto}</MenuItem>
            ))}
          </TextField>
        </Grid>
        {tipo === 'TRANSFERENCIA' && (
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Almacén destino"
                       value={f.almacenDestinoId || ''} onChange={upd('almacenDestinoId')}>
              {almacenes.filter(a => a.almacenId !== f.almacenId).map(a => (
                <MenuItem key={a.almacenId} value={a.almacenId}>{a.nombreCompleto}</MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        {(tipo === 'ENTRADA' || tipo === 'SALIDA' || tipo === 'TRANSFERENCIA') && (
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Cantidad" type="number" required
                       value={f.cantidad} onChange={upd('cantidad')}
                       autoFocus />
          </Grid>
        )}
        {tipo === 'AJUSTE' && (
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Stock contado (final)" type="number" required
                       value={f.stockFinalContado} onChange={upd('stockFinalContado')}
                       autoFocus
                       helperText="El sistema calcula la diferencia con el stock actual." />
          </Grid>
        )}
        {tipo === 'ENTRADA' && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Costo unitario (opcional)" type="number"
                         value={f.costoUnitario} onChange={upd('costoUnitario')}
                         InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                         helperText="Si difiere, actualiza el costo promedio." />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Motivo"
                         value={f.motivo} onChange={upd('motivo')}>
                <MenuItem value="COMPRA">Compra a proveedor</MenuItem>
                <MenuItem value="DEVOLUCION_CLIENTE">Devolución de cliente</MenuItem>
                <MenuItem value="ENTRADA_MANUAL">Entrada manual</MenuItem>
                <MenuItem value="OTRO">Otro</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Documento (opcional)"
                         value={f.documentoNumero} onChange={upd('documentoNumero')}
                         placeholder="Ej. FE-001-1234" />
            </Grid>
          </>
        )}
        {tipo === 'SALIDA' && (
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Motivo"
                       value={f.motivo} onChange={upd('motivo')}>
              <MenuItem value="MERMA">Merma / dañado</MenuItem>
              <MenuItem value="CONSUMO_INTERNO">Consumo interno</MenuItem>
              <MenuItem value="OBSEQUIO">Obsequio</MenuItem>
              <MenuItem value="ROBO">Robo</MenuItem>
              <MenuItem value="DEVOLUCION_PROVEEDOR">Devolución a proveedor</MenuItem>
              <MenuItem value="OTRO">Otro</MenuItem>
            </TextField>
          </Grid>
        )}
        {(tipo === 'SALIDA' || tipo === 'AJUSTE' || tipo === 'TRANSFERENCIA') && (
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={2} label="Notas (opcional)"
                       value={f.notas} onChange={upd('notas')} />
          </Grid>
        )}
      </Grid>
      <Divider sx={{ my: 2 }} />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit() || enviando}
                color={tipo === 'ENTRADA' ? 'success' : tipo === 'SALIDA' ? 'error' :
                       tipo === 'TRANSFERENCIA' ? 'info' : 'primary'}>
          {enviando ? 'Registrando…' : 'Registrar'}
        </Button>
      </Stack>
    </Box>
  );
}

export default DialogMovimientosProducto;
